// The cue catalogue (KOLONY-Audio-Design.md §4/§5) — DATA only, no Web Audio, no DOM.
//
// Every row carries the three properties the design doc says decide whether a cue works:
// which bus owns it, how loud it sits relative to its neighbours, and how often it may fire.
// `family` is what §2.2's scheduler collapses: one roll can emit DICE_ROLLED, five
// SUPPLY_SHORT and one HARVEST_PAID per paid seat, so within a single batch only the
// highest-priority member of a family is allowed to sound.

export type Bus = 'music' | 'sfx' | 'ambient';

export interface CueSpec {
  /**
   * The file name, and the BUS decides where it lives, because that is where the engine
   * fetches it. On `sfx`: `audio/<id>.webm` (Opus) with an `audio/<id>.m4a` (AAC) fallback. On
   * `music` or `ambient`: `audio/music/<id>.webm`, Opus alone (D155), deferred media — so a
   * one-shot moved onto the music bus MOVES DIRECTORY with it (D283; cueFiles.test.ts walks it).
   */
  readonly id: string;
  readonly bus: Bus;
  /** Per-cue trim in dB — every file is peak-normalised, so ALL relative level lives here. */
  readonly gainDb: number;
  /** Round-robin variants; the played file is `${id}-${n}` when > 1. */
  readonly variants?: number;
  /** Rate limit. A cue asked for again inside this window is dropped. */
  readonly minIntervalMs?: number;
  /** Within one batch, only the highest `priority` of a family sounds. */
  readonly family?: string;
  readonly priority?: number;
  readonly loop?: boolean;
}

// Levels are deliberately conservative: the design doc's "silence is a resource" only works
// if the bed and the constant cues sit well under the ones that carry information.
export const CUES = {
  'ui-tap': { id: 'ui-tap', bus: 'sfx', gainDb: -20, minIntervalMs: 40 },
  dice: { id: 'dice', bus: 'sfx', gainDb: -6, variants: 3, family: 'roll', priority: 10 },
  'roll-dry': { id: 'roll-dry', bus: 'sfx', gainDb: -14, family: 'roll-outcome', priority: 10 },
  // H15-S4: the near-miss — a dry roll that landed ±1 off the viewer's own number. One
  // muted knock a step DOWN from roll-dry's; priority BETWEEN dry (10) and pays (20), so
  // the family collapse does the whole job and the scheduler learns nothing new.
  'roll-pays': { id: 'roll-pays', bus: 'sfx', gainDb: -8, family: 'roll-outcome', priority: 20 },
  resource: { id: 'resource', bus: 'sfx', gainDb: -12, family: 'gain', priority: 10 },
  'your-turn': { id: 'your-turn', bus: 'sfx', gainDb: -8 },
  'clock-tick': { id: 'clock-tick', bus: 'sfx', gainDb: -16, minIntervalMs: 250 },
  seven: { id: 'seven', bus: 'sfx', gainDb: -5, family: 'roll-outcome', priority: 30 },
  'build-road': { id: 'build-road', bus: 'sfx', gainDb: -10, family: 'build', priority: 10 },
  'build-settlement': { id: 'build-settlement', bus: 'sfx', gainDb: -8, family: 'build', priority: 20 },
  'build-city': { id: 'build-city', bus: 'sfx', gainDb: -6, family: 'build', priority: 30 },
  'trade-offer': { id: 'trade-offer', bus: 'sfx', gainDb: -6 },
  'game-over': { id: 'game-over', bus: 'sfx', gainDb: -4 },
  'bandit-blocked': { id: 'bandit-blocked', bus: 'sfx', gainDb: -10, family: 'roll-outcome', priority: 25 },
  'bandit-drop': { id: 'bandit-drop', bus: 'sfx', gainDb: -8 },
  'steal-lose': { id: 'steal-lose', bus: 'sfx', gainDb: -8, family: 'steal', priority: 20 },
  'steal-gain': { id: 'steal-gain', bus: 'sfx', gainDb: -8, family: 'steal', priority: 20 },
  discard: { id: 'discard', bus: 'sfx', gainDb: -8 },
  'card-bought': { id: 'card-bought', bus: 'sfx', gainDb: -10, family: 'gain', priority: 20 },
  'vp-gain': { id: 'vp-gain', bus: 'sfx', gainDb: -7 },
  // D244: both trade cues were REPLACED. Measured, the old pair were the two worst files in
  // the set on the D155 screen — -0.1 dB and -2.1 dB of energy above 5 kHz (the good cues sit
  // at -33 to -47), i.e. very nearly all hiss — AND the loudest things in the game at -17.9 and
  // -13.4 LUFS against a -22..-27 field. The replacements measure -43.9/-45.7 dB and -23.2/-27.9
  // LUFS. `supply-trade`'s trim moves -11 -> -6 because its file is no longer 10 dB hot: the
  // old number was compensating for the sound, and taking the compensation out with it would
  // have dropped a frequent cue 14 dB and read as "the sound is gone".
  'trade-accepted': { id: 'trade-accepted', bus: 'sfx', gainDb: -9, family: 'gain', priority: 15 },
  'supply-trade': { id: 'supply-trade', bus: 'sfx', gainDb: -6, family: 'gain', priority: 15 },
  'clock-auto': { id: 'clock-auto', bus: 'sfx', gainDb: -12 },
  surf: { id: 'surf', bus: 'ambient', gainDb: -26, loop: true },
  'music-menu': { id: 'music-menu', bus: 'music', gainDb: -16, loop: true },
  'music-game': { id: 'music-game', bus: 'music', gainDb: -22, loop: true },
  'music-victory': { id: 'music-victory', bus: 'music', gainDb: -8 },
  // H14 promotion. The bell is the reveal's attack and the fanfare rides behind it, so they
  // are deliberately NOT one family: collapsing them would silence half the moment. Both are
  // rate-limited hard — a rank changes at most once per game, and anything faster is a bug.
  'rank-bell': { id: 'rank-bell', bus: 'sfx', gainDb: -9, minIntervalMs: 4000 },
  'rank-up': { id: 'rank-up', bus: 'music', gainDb: -6, minIntervalMs: 4000 },
  // H20 (D279): the Open Sea's three cues, all in the BUILD family because each is what one
  // build (or a ship's move) turned out to MEAN. A ship is its own launch, never the road's
  // knock; a build that lifts the fog is the discovery; a settlement that makes Landfall is the
  // Landfall. The collapse keeps the news and drops the knock under it, so one action still
  // sounds once. Trims are measured after Opus (-25.1 / -23.4 / -15.9 LUFS as files) to put
  // each where its peers play: the launch with the road (~-30), the discovery a touch above a
  // settlement (~-29), Landfall with the city (~-28), since it replaces a settlement's knock.
  'ship-launched': { id: 'ship-launched', bus: 'sfx', gainDb: -5, family: 'build', priority: 10 },
  'fog-lifted': { id: 'fog-lifted', bus: 'sfx', gainDb: -6, family: 'build', priority: 35 },
  landfall: { id: 'landfall', bus: 'sfx', gainDb: -12, family: 'build', priority: 40 },
  // D285: the gold choice lands as a dull bell tap — its own cue, in the GAIN family just above
  // the generic resource, and never a coin (a clink is the >5 kHz hiss D155 rejects). -22.0 LUFS
  // as a file; the trim sits it between a resource (~-36) and a build knock (~-30).
  'gold-chosen': { id: 'gold-chosen', bus: 'sfx', gainDb: -11, family: 'gain', priority: 12 },
} as const satisfies Record<string, CueSpec>;

export type CueId = keyof typeof CUES;

export const cueSpec = (id: CueId): CueSpec => CUES[id];

/** The one-shots worth having in memory before the first roll; music/ambient stay lazy. */
export const PRELOAD: readonly CueId[] = [
  'ui-tap',
  'dice',
  'roll-dry',
  'roll-pays',
  'resource',
  'your-turn',
  'seven',
  'build-road',
  'build-settlement',
  'build-city',
];
